import { usePetStore } from '@/features/pet/pet.store';
import { useFitText } from '@/shared/hooks/useFitText';

import { LsPetLoader } from './LsPetLoader';
import styles from './pet-print.module.scss';

/**
 * Кегль имени в долях ширины зоны печати.
 *
 * Это не «предел на крайний случай», а рабочий размер: кличка набирается
 * им всегда и стоит по центру. Уменьшается она только тогда, когда
 * иначе не влезает по ширине.
 *
 * Так было не всегда, и прежнее поведение заказчик забраковал. Кегль
 * подбирался так, чтобы кличка любой длины упиралась в обе границы зоны.
 * На длинных это выглядит хорошо, на коротких — нет: у трёх букв размер
 * оказывался вдвое больше, чем у шести, надпись занимала пол-изделия
 * и выдавливала питомца вниз.
 *
 * Число — из замера теми самыми файлами гарнитур, которыми печатается
 * принт (scripts не нужен, замер в истории захода ID-189). Какая доля
 * ширины зоны нужна кеглю, чтобы кличка встала от края до края:
 *
 * |             | Prata | Comfortaa | Yeseva One |
 * |-------------|-------|-----------|------------|
 * | АЯ          | 0.637 | 0.658     | 0.678      |
 * | БИМ         | 0.353 | 0.360     | 0.383      |
 * | ПЕТТИ       | 0.235 | 0.249     | 0.267      |
 * | БАРСИК      | 0.201 | 0.208     | 0.221      |
 * | БАРБАРИСКА  | 0.122 | 0.127     | 0.134      |
 *
 * 0.24 — это размер пятибуквенной клички, той самой «ПЕТТИ» с образца,
 * который заказчик показал как верный. Всё, что короче, набирается тем же
 * кеглем и просто центруется; всё, что длиннее, ужимается по ширине.
 *
 * NOTE: пара к `name_max_ratio` в letsshine_shared/pet_poster.py. Разойдутся —
 * на экране будет один размер, в печати другой.
 */
const NAME_MAX_RATIO = 0.24;

/**
 * Собранный принт: снимок питомца, имя, год и свободные строки.
 *
 * Это единственное место, где принт складывается на экране. Повторять
 * ту же композицию на каждом экране нельзя: собранный принт человек видит
 * на шаге принта, на основе и на результате, и три копии разъедутся
 * при первой же правке.
 *
 * NOTE: пара к сборке принта в боте (`utils/pet_poster.py`). Порядок
 * и пропорции обязаны совпадать, иначе макет на сайте и в печати разный.
 */
export function LsPetPrint() {
  const photoUrl = usePetStore((s) => s.photoUrl);
  const cutoutUrl = usePetStore((s) => s.cutoutUrl);
  const isCutting = usePetStore((s) => s.isCutting);
  const name = usePetStore((s) => s.name);
  const year = usePetStore((s) => s.year);
  const lines = usePetStore((s) => s.lines);
  const hasBackground = usePetStore((s) => s.hasBackground);
  const inkColor = usePetStore((s) => s.inkColor);
  const font = usePetStore((s) => s.font);

  const label = name.trim().toUpperCase();

  // Кегль имени считается до выходов из функции: правило вызова хуков
  // не допускает условного вызова, а выше нас ждут два выхода —
  // пустой снимок и ожидание вырезания.
  //
  // Ссылка при этом может никуда не попасть, и это нормально:
  // пока имени на экране нет, мерить нечего.
  const nameRef = useFitText<HTMLSpanElement>({ maxRatio: NAME_MAX_RATIO });

  if (!photoUrl) return null;

  // Пока фон вырезается, в зоне печати стоит ожидание со знаком
  // заказчика, а не приглушённый снимок. Приглушённый читался
  // двусмысленно: непонятно, идёт работа или так и задумано.
  if (isCutting) return <LsPetLoader />;

  // Раскладка повторяет бот: имя прописными сверху, под ним строка
  // «EST.» слева и год справа по краям блока имени, ниже питомец.
  //
  // Строкой «EST. 2024» по центру это было неверно: на образце заказчика
  // подписи разнесены по краям и держат ширину имени, как выходные
  // данные на плакате. Сведённые в одну строку, они читались подписью
  // к фотографии, а не частью композиции.
  //
  // Свободные строки идут под снимком — они наше дополнение, в боте их
  // нет вовсе, и над питомцем они перетягивали бы внимание на себя.
  //
  // NOTE: пара к utils/pet_poster.py (_draw_captions, PosterStyle).
  // Разойдутся — человек увидит на сайте одно, а получит другое.
  const tail = lines.filter(Boolean);

  return (
    <span className={styles['ls-pet-print']} data-ink={inkColor} data-font={font}>
      {/*
        Кличка тянется от левой границы зоны печати до правой при любой
        длине: кегль подбирается по настоящей ширине строки (см. useFitText).
        Прежде он считался по числу знаков, и до краёв не доходило ничего —
        у букв разная ширина, и оценка всегда была с запасом.
      */}
      {label && (
        <span ref={nameRef} className={styles['ls-pet-print__name']}>
          {label}
        </span>
      )}

      {/*
        «EST.» и год по краям блока, как в боте. Год без имени не бывает:
        строка выходных данных без заголовка над ней выглядит обрывком.
      */}
      {label && year && (
        <span className={styles['ls-pet-print__est']}>
          <span className={styles['ls-pet-print__est-word']}>EST.</span>
          <span className={styles['ls-pet-print__est-year']}>{year}</span>
        </span>
      )}

      {/*
        С галочкой «фон» — исходный снимок, без неё — вырезанный.
        Пока вырезание идёт, показываем исходник: пустое место вместо
        питомца человек читает как поломку, а не как ожидание.
      */}
      {/*
        Снимок лежит в своей площадке, а не растягивается сам.
        `flex-grow` на картинке не заставляет её расти: рамка
        остаётся по содержимому, и питомец выходил мелким,
        сколько бы места под ним ни оставалось.
      */}
      <span className={styles['ls-pet-print__stage']}>
        <img
          className={[
            styles['ls-pet-print__photo'],
            // Скругление — у снимка с фоном. У вырезанного углов нет,
            // и скругление срезало бы ухо или хвост.
            hasBackground || !cutoutUrl ? styles['ls-pet-print__photo--has-bg'] : '',
          ]
            .join(' ')
            .trim()}
          src={hasBackground ? photoUrl : (cutoutUrl ?? photoUrl)}
          alt=""
          draggable={false}
        />
      </span>

      {tail.map((text) => (
        <span key={text} className={styles['ls-pet-print__line']}>
          {/* Капсом, как кличка: так заказчик видит принт. Печатный файл — так же. */}
          {text.toUpperCase()}
        </span>
      ))}
    </span>
  );
}
